import { MD3LightTheme, MD3DarkTheme, useTheme } from 'react-native-paper';

// Kinetic Blueprint — "The Architectural Engine"
// Primary: deep navy #063669, tonal surfaces, no 1px borders
export const colors = {
  // Brand
  primary: '#063669',
  primaryContainer: '#274e82',
  primaryFixed: '#d5e3ff',
  primaryFixedDim: '#a7c8ff',
  onPrimary: '#ffffff',
  onPrimaryContainer: '#9cc0fb',
  onPrimaryFixed: '#001b3c',
  onPrimaryFixedVariant: '#1f477b',

  secondary: '#515f74',
  secondaryContainer: '#d5e3fc',
  secondaryFixed: '#d5e3fc',
  secondaryFixedDim: '#b9c7df',
  onSecondary: '#ffffff',
  onSecondaryContainer: '#57657a',
  onSecondaryFixed: '#0d1c2e',
  onSecondaryFixedVariant: '#3a485b',

  tertiary: '#651f00',
  tertiaryContainer: '#8b2e01',
  tertiaryFixed: '#ffdbcf',
  tertiaryFixedDim: '#ffb59a',
  onTertiary: '#ffffff',
  onTertiaryContainer: '#ffaa8a',
  onTertiaryFixed: '#380d00',
  onTertiaryFixedVariant: '#802900',

  error: '#ba1a1a',
  errorContainer: '#ffdad6',
  onError: '#ffffff',
  onErrorContainer: '#93000a',

  // Semantic — success (MD3 doesn't ship one; tones follow the same 40/100/90/10 pattern as primary)
  success: '#2e7d32',
  successContainer: '#b6f0bb',
  onSuccess: '#ffffff',
  onSuccessContainer: '#002106',

  // Surfaces (use in stacking order for depth — no borders between layers)
  background: '#f9f9ff',
  surface: '#f9f9ff',
  surfaceBright: '#f9f9ff',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerLow: '#f0f3ff',
  surfaceContainer: '#e7eeff',
  surfaceContainerHigh: '#dee8ff',
  surfaceContainerHighest: '#d8e3fb',
  surfaceVariant: '#d8e3fb',
  surfaceDim: '#cfdaf2',
  surfaceTint: '#3a5f94',

  // Text
  onSurface: '#111c2d',
  onSurfaceVariant: '#434653',
  onBackground: '#111c2d',
  inverseSurface: '#263143',
  inverseOnSurface: '#ecf1ff',
  inversePrimary: '#a7c8ff',

  // Utility
  outline: '#737784',
  outlineVariant: '#c3c6d5',

  // Elevation (kept for Paper compatibility — use tonal surfaces instead)
  elevation: {
    level0: 'transparent',
    level1: '#ffffff',
    level2: '#f0f3ff',
    level3: '#e7eeff',
    level4: '#dee8ff',
    level5: '#d8e3fb',
  },
};

export const lightTheme = {
  ...MD3LightTheme,
  colors: {
    ...MD3LightTheme.colors,
    primary: colors.primary,
    primaryContainer: colors.primaryContainer,
    secondary: colors.secondary,
    secondaryContainer: colors.secondaryContainer,
    tertiary: colors.tertiary,
    tertiaryContainer: colors.tertiaryContainer,
    error: colors.error,
    errorContainer: colors.errorContainer,
    onPrimary: colors.onPrimary,
    onPrimaryContainer: colors.onPrimaryContainer,
    onSecondary: colors.onSecondary,
    onSecondaryContainer: colors.onSecondaryContainer,
    onTertiary: colors.onTertiary,
    onTertiaryContainer: colors.onTertiaryContainer,
    onError: colors.onError,
    onErrorContainer: colors.onErrorContainer,
    success: colors.success,
    successContainer: colors.successContainer,
    onSuccess: colors.onSuccess,
    onSuccessContainer: colors.onSuccessContainer,
    background: colors.background,
    surface: colors.surfaceContainerLowest,
    surfaceVariant: colors.surfaceContainerLow,
    onSurface: colors.onSurface,
    onSurfaceVariant: colors.onSurfaceVariant,
    outline: colors.outline,
    outlineVariant: colors.outlineVariant,
    elevation: colors.elevation,
  },
  roundness: 6,
  fonts: {
    ...MD3LightTheme.fonts,
    displayLarge: {
      ...MD3LightTheme.fonts.displayLarge,
      fontFamily: 'Manrope-Bold',
      fontSize: 57,
    },
    displayMedium: {
      ...MD3LightTheme.fonts.displayMedium,
      fontFamily: 'Manrope-Bold',
      fontSize: 44,
    },
    displaySmall: {
      ...MD3LightTheme.fonts.displaySmall,
      fontFamily: 'Manrope-Bold',
      fontSize: 36,
    },
    headlineLarge: {
      ...MD3LightTheme.fonts.headlineLarge,
      fontFamily: 'Manrope-SemiBold',
      fontSize: 28,
    },
    headlineMedium: {
      ...MD3LightTheme.fonts.headlineMedium,
      fontFamily: 'Manrope-SemiBold',
      fontSize: 22,
    },
    headlineSmall: {
      ...MD3LightTheme.fonts.headlineSmall,
      fontFamily: 'Manrope-SemiBold',
      fontSize: 18,
    },
    titleLarge: {
      ...MD3LightTheme.fonts.titleLarge,
      fontFamily: 'Manrope-SemiBold',
      fontSize: 22,
    },
    titleMedium: {
      ...MD3LightTheme.fonts.titleMedium,
      fontFamily: 'Manrope-SemiBold',
      fontSize: 16,
    },
    titleSmall: {
      ...MD3LightTheme.fonts.titleSmall,
      fontFamily: 'Manrope-Medium',
      fontSize: 14,
    },
    bodyLarge: {
      ...MD3LightTheme.fonts.bodyLarge,
      fontFamily: 'Inter-Regular',
      fontSize: 16,
    },
    bodyMedium: {
      ...MD3LightTheme.fonts.bodyMedium,
      fontFamily: 'Inter-Regular',
      fontSize: 14,
    },
    bodySmall: {
      ...MD3LightTheme.fonts.bodySmall,
      fontFamily: 'Inter-Regular',
      fontSize: 12,
    },
    labelLarge: {
      ...MD3LightTheme.fonts.labelLarge,
      fontFamily: 'Inter-Medium',
      fontSize: 14,
    },
    labelMedium: {
      ...MD3LightTheme.fonts.labelMedium,
      fontFamily: 'Inter-Medium',
      fontSize: 12,
    },
    labelSmall: {
      ...MD3LightTheme.fonts.labelSmall,
      fontFamily: 'Inter-Medium',
      fontSize: 11,
    },
  },
};

// Dark palette — Kinetic Blueprint dark variant
export const darkColors = {
  primary: '#a7c8ff',
  primaryContainer: '#1f477b',
  primaryFixed: '#d5e3ff',
  primaryFixedDim: '#a7c8ff',
  onPrimary: '#001b3c',
  onPrimaryContainer: '#d5e3ff',
  onPrimaryFixed: '#001b3c',
  onPrimaryFixedVariant: '#1f477b',

  secondary: '#b9c7df',
  secondaryContainer: '#3a485b',
  secondaryFixed: '#d5e3fc',
  secondaryFixedDim: '#b9c7df',
  onSecondary: '#243347',
  onSecondaryContainer: '#d5e3fc',
  onSecondaryFixed: '#0d1c2e',
  onSecondaryFixedVariant: '#3a485b',

  tertiary: '#ffb59a',
  tertiaryContainer: '#6b2100',
  tertiaryFixed: '#ffdbcf',
  tertiaryFixedDim: '#ffb59a',
  onTertiary: '#5c1400',
  onTertiaryContainer: '#ffdbcf',
  onTertiaryFixed: '#380d00',
  onTertiaryFixedVariant: '#802900',

  error: '#ffb4ab',
  errorContainer: '#93000a',
  onError: '#690005',
  onErrorContainer: '#ffdad6',

  // Semantic — success (dark variant: tones 80/20/30/90)
  success: '#9bd49f',
  successContainer: '#1b5e20',
  onSuccess: '#003912',
  onSuccessContainer: '#b6f0bb',

  background: '#111c2d',
  surface: '#111c2d',
  surfaceBright: '#2a3750',
  surfaceContainerLowest: '#0d1827',
  surfaceContainerLow: '#192339',
  surfaceContainer: '#1d2843',
  surfaceContainerHigh: '#22304e',
  surfaceContainerHighest: '#2d3c5a',
  surfaceVariant: '#3a485b',
  surfaceDim: '#111c2d',
  surfaceTint: '#a7c8ff',

  onSurface: '#dce3f5',
  onSurfaceVariant: '#b0b8cc',
  onBackground: '#dce3f5',
  inverseSurface: '#dce3f5',
  inverseOnSurface: '#263143',
  inversePrimary: '#063669',

  outline: '#8b93a5',
  outlineVariant: '#3a485b',

  elevation: {
    level0: 'transparent',
    level1: '#192339',
    level2: '#1d2843',
    level3: '#22304e',
    level4: '#243350',
    level5: '#2d3c5a',
  },
};

export const darkTheme = {
  ...MD3DarkTheme,
  dark: true,
  colors: {
    ...MD3DarkTheme.colors,
    primary: darkColors.primary,
    primaryContainer: darkColors.primaryContainer,
    secondary: darkColors.secondary,
    secondaryContainer: darkColors.secondaryContainer,
    tertiary: darkColors.tertiary,
    tertiaryContainer: darkColors.tertiaryContainer,
    error: darkColors.error,
    errorContainer: darkColors.errorContainer,
    onPrimary: darkColors.onPrimary,
    onPrimaryContainer: darkColors.onPrimaryContainer,
    onSecondary: darkColors.onSecondary,
    onSecondaryContainer: darkColors.onSecondaryContainer,
    onTertiary: darkColors.onTertiary,
    onTertiaryContainer: darkColors.onTertiaryContainer,
    onError: darkColors.onError,
    onErrorContainer: darkColors.onErrorContainer,
    success: darkColors.success,
    successContainer: darkColors.successContainer,
    onSuccess: darkColors.onSuccess,
    onSuccessContainer: darkColors.onSuccessContainer,
    background: darkColors.background,
    surface: darkColors.surfaceContainerLowest,
    surfaceVariant: darkColors.surfaceVariant,
    onSurface: darkColors.onSurface,
    onSurfaceVariant: darkColors.onSurfaceVariant,
    outline: darkColors.outline,
    outlineVariant: darkColors.outlineVariant,
    elevation: darkColors.elevation,
  },
  roundness: 6,
  fonts: lightTheme.fonts,
};

export type AppTheme = typeof lightTheme;

// Use this in any component that touches our custom palette extensions
// (success, etc.). Plain useTheme() from react-native-paper still works
// for components that only touch MD3 tokens.
export const useAppTheme = () => useTheme<AppTheme>();

export default lightTheme;
